import { useLayoutEffect, useRef, useState } from "react";
import type { Item, Person } from "./api.ts";
import { ChoiceFilter } from "./ChoiceFilter.tsx";
import { FIND_PAST, faceNames, finds, inOrder } from "./faces.ts";
import type { Kind } from "./format.ts";
import { KindFilter } from "./KindFilter.tsx";
import { Avatar, Chip, Icon } from "./souvenir.ts";

// The map's filters (features/map.md): whose wants show, yes only, one friend's picks, and the kinds.
// Whose wants, for any group size: a chip each (Everyone, You, then everyone by first name) up to
// CHIPS_UP_TO people; from one more, one "Who" chip opening a menu, with "Find someone" past FIND_PAST.
// Friends' picks fold the same way on a computer past FRIENDS_UP_TO friends; a phone has one chip with
// the phone's own picker. The filters keep to one row on a computer and two on a phone, measured: where the
// chips would take more (long first names, friends' picks, a narrow window), a computer folds friends' picks
// into their menu first, then people into Who; a phone folds people into Who.

export const CHIPS_UP_TO = 4;
export const FRIENDS_UP_TO = 3;
// How many rows the filters may take on a computer and on a phone.
const WIDE_ROWS = 1;
const PHONE_ROWS = 2;

export function MapFilters({
  members,
  you,
  person,
  onPerson,
  yesOnly,
  onYesOnly,
  friends,
  friend,
  onFriend,
  items,
  kinds,
  onKinds,
  wide,
  whose = true,
}: {
  members: Person[];
  // Whether whose wants can be picked (a shared trip's view without anyone's ratings has none to pick).
  whose?: boolean;
  you: string;
  // "all", or whose wants show.
  person: string;
  onPerson: (person: string) => void;
  yesOnly: boolean;
  onYesOnly: (yesOnly: boolean) => void;
  // Friends whose picks are in the plan, with how many, most first.
  friends: [string, number][];
  friend: string | null;
  onFriend: (friend: string | null) => void;
  // What the What filter counts kinds among.
  items: Item[];
  kinds: Kind[];
  onKinds: (kinds: Kind[]) => void;
  wide: boolean;
}) {
  const people = inOrder(members, you);
  const names = faceNames(members, you);
  const label = (p: Person) => names.get(p.id) ?? p.name;

  // Whether the chips took more rows than they may here: then, on a computer, friends' picks fold into their
  // menu (1), and if that's still too many, people into Who (2); on a phone, people into Who. Until the width,
  // the people or the friends change.
  const root = useRef<HTMLFieldSetElement>(null);
  const [crowded, setCrowded] = useState<{ width: number; key: string; level: 1 | 2 } | null>(null);
  const key = [
    wide ? "wide" : "phone",
    people.map((p) => `${p.id}:${p.name}`).join("|"),
    friends.map(([f]) => f).join("|"),
  ].join("#");
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const resized = new ResizeObserver(() => setWidth(Math.round(el.clientWidth)));
    resized.observe(el);
    return () => resized.disconnect();
  }, []);
  const level = crowded !== null && crowded.width === width && crowded.key === key ? crowded.level : 0;
  const folded = members.length > CHIPS_UP_TO || level === 2;
  const friendsFolded = wide && (friends.length > FRIENDS_UP_TO || level >= 1);
  useLayoutEffect(() => {
    const el = root.current;
    if (!el || folded || (whose === false && friendsFolded)) return;
    const tops = new Set<number>();
    for (const child of el.children) {
      const box = child.getBoundingClientRect();
      if (box.width > 0 && getComputedStyle(child).position !== "absolute") tops.add(Math.round(box.top));
    }
    if (tops.size <= (wide ? WIDE_ROWS : PHONE_ROWS)) return;
    setCrowded({ width, key, level: wide && !friendsFolded && friends.length > 0 ? 1 : 2 });
  });

  const who = folded ? (
    <ChoiceFilter
      title="Who"
      legend="Who to show"
      className="tm-who"
      compact={!wide}
      value={person}
      onPick={onPerson}
      choices={[
        { value: "all", label: "Everyone", lead: <Icon name="people" size={16} /> },
        // The menu's rows say whole names; the chip, the first name as People's faces do.
        ...people.map((p) => ({
          value: p.id,
          label: p.id === you ? "You" : p.name,
          short: label(p),
          lead: <Avatar name={p.name} color={p.color} {...(p.src ? { src: p.src } : {})} size={24} />,
        })),
      ]}
      {...(members.length > FIND_PAST
        ? {
            find: {
              placeholder: "Find someone",
              finds: (value: string, query: string) => {
                const p = members.find((m) => m.id === value);
                return p ? finds(p, query, you) : false;
              },
              none: (query: string) => `No one in the trip is called “${query}”.`,
            },
          }
        : {})}
    />
  ) : (
    <>
      <Chip icon="people" selected={person === "all"} onClick={() => onPerson("all")}>
        Everyone
      </Chip>
      {people.map((p) => (
        <Chip key={p.id} person={p} selected={person === p.id} onClick={() => onPerson(p.id)}>
          {label(p)}
        </Chip>
      ))}
    </>
  );

  return (
    <fieldset className="tm-filters" ref={root}>
      <legend className="tm-hidden">{whose ? "Whose wants" : "What to show"}</legend>
      {whose && who}
      {whose && (
        <Chip icon="star" selected={yesOnly} onClick={() => onYesOnly(!yesOnly)}>
          Yes only
        </Chip>
      )}
      {friendsFolded ? (
        <ChoiceFilter
          title="Friends' picks"
          legend="Whose picks to show"
          compact={false}
          value={friend ?? ""}
          onPick={(f) => onFriend(f || null)}
          choices={[
            { value: "", label: "Everything", short: "Friends' picks", lead: <Icon name="heart" size={16} /> },
            ...friends.map(([f, n]) => ({
              value: f,
              label: `${f}'s picks`,
              lead: <Icon name="heart" size={16} />,
              count: n,
            })),
          ]}
        />
      ) : wide ? (
        friends.map(([f]) => (
          <Chip key={f} icon="heart" selected={friend === f} onClick={() => onFriend(friend === f ? null : f)}>
            {f}'s picks
          </Chip>
        ))
      ) : friends.length > 0 ? (
        // On a phone, one chip for every friend's picks, so the filters stay on two rows; the phone's own
        // picker lists the friends.
        <label className={`sv-chip sv-chip--pick tm-chip-select${friend ? " is-selected" : ""}`}>
          <Icon name="heart" size={16} />
          <span>{friend ? `${friend}'s picks` : "Friends' picks"}</span>
          <select aria-label="Friends' picks" value={friend ?? ""} onChange={(e) => onFriend(e.target.value || null)}>
            <option value="">Everything</option>
            {friends.map(([f]) => (
              <option key={f} value={f}>
                {f}'s picks
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <KindFilter items={items} kinds={kinds} compact={!wide} onChange={onKinds} />
    </fieldset>
  );
}
